import React, { useEffect, useMemo, useState } from "react";

import { Button } from "@/components/ui/button";

import SearchInput from "@/components/ui/search-input";
import "@xyflow/react/dist/style.css";
import { Check, Copy, Filter, Pencil, Repeat2, Trash2, UserRoundPen } from "lucide-react";
import * as projectsService from "./service/projects";
import { useSearchParams } from "react-router-dom";
import CustomTable from "../../components/custom-table";
import SimpleTooltip from "../../components/simple-tooltip";
import CollaboratorsDialog from "./components/CollaboratorsDialog";
import CreateProjectDialog from "./components/CreateProjectDialog";
import EditProjectDialog from "./components/EditProjectDialog";
import { useGetProjects } from "./hooks/projects";
import useProjectStore from "./store/projects";

const ApiKeyCell = ({ projectId }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      const data = await projectsService.getApiKey(projectId);
      await navigator.clipboard.writeText(data.key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // No API key for this project
    }
  };

  return (
    <Button variant="borderless" size="sm" className="h-7 px-2 text-xs text-secondary hover:text-white" onClick={handleCopy}>
      {copied ? (
        <><Check className="size-3 mr-1 text-green-500" /> Copied</>
      ) : (
        <><Copy className="size-3 mr-1" /> Copy Key</>
      )}
    </Button>
  );
};

const columns = [
  {
    title: "Project Name",
    accessor: "name",
  },
  {
    title: "Description",
    accessor: "description",
  },
  {
    title: "API Key",
    accessor: "apiKey",
    render: (row) => <ApiKeyCell projectId={row.id} />,
  },
  {
    title: "Created At",
    accessor: "createdAt",
  },
  {
    title: "Role",
    accessor: "role",
  },
];

const Projects = () => {
  const { projects, pagination, refreshProjects, loading } = useGetProjects();
  const { openModal, modals, closeModal, setCurrentProject } =
    useProjectStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const data = useMemo(() => {
    return projects.map((project) => ({
      id: project.id,
      name: project.name,
      description: project.description,
      role: project.role,
      createdAt: new Date(project.createdAt).toLocaleString(),
    }));
  }, [projects]);

  useEffect(() => {
    const showCreate = searchParams.get("create");
    if (showCreate === "true") {
      openModal("create");
    }
  }, [searchParams]);

  const handlePageChange = (newPage) => {
    refreshProjects(newPage);
  };

  const handlePageSizeChange = (newSize) => {
    // setPagination((prev) => ({ ...prev, size: newSize, page: 0 }));
  };

  return (
    <div className="relative w-full">
      <div className="flex h-[32px] absolute w-full z-[1]">
        <CreateProjectDialog />
        <div className="ml-auto flex items-center space-x-2 ">
          <SearchInput />
          <Button
            variant="ghost"
            size="icon"
            className="rounded h-auto bg-muted"
          >
            <Filter className="text-gray-400" />
          </Button>
        </div>
      </div>
      <div className="mt-14">
        <CustomTable
          isLoading={loading}
          columns={columns}
          data={data}
          paginated={true}
          pagination={pagination}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          actions={[
            (row) =>
              row.role === "OWNER" ? (
                <SimpleTooltip label="Edit Project" side="left">
                  <Button
                    variant="borderless"
                    size="sm"
                    className="text-secondary hover:text-white-500 w-full flex justify-start items-center"
                    onClick={() => {
                      setCurrentProject(row);
                      openModal("edit");
                    }}
                  >
                    <Pencil className="text-gray-400" />
                    Edit Project
                  </Button>
                </SimpleTooltip>
              ) : null,
            (row) =>
              row.role === "OWNER" ? (
                <SimpleTooltip label="Edit Collaborators" side="left">
                  <Button
                    variant="borderless"
                    size="sm"
                    className="text-secondary hover:text-white-500 w-full flex justify-start items-center"
                    onClick={() => {
                      setCurrentProject(row);
                      openModal("collaborators");
                    }}
                  >
                    <UserRoundPen className="text-gray-400" />
                    Edit Collaborators
                  </Button>
                </SimpleTooltip>
              ) : null,
            (row) =>
              row.role === "OWNER" ? (
                <SimpleTooltip label="Regenerate API Key" side="left">
                  <Button
                    variant="borderless"
                    size="sm"
                    className="text-secondary hover:text-white-500 w-full flex justify-start items-center"
                  >
                    <Repeat2 className="text-gray-400" />
                    Regenerate API Key
                  </Button>
                </SimpleTooltip>
              ) : null,
            (row) =>
              row.role === "OWNER" ? (
                <SimpleTooltip label="Delete" side="left">
                  <Button
                    variant="borderless"
                    size="sm"
                    className="text-secondary hover:text-white-500 w-full flex justify-start items-center"
                  >
                    <Trash2 className="text-red-500" />
                    Delete
                  </Button>
                </SimpleTooltip>
              ) : null,
          ]}
        />
      </div>
      <EditProjectDialog />
      <CollaboratorsDialog />
    </div>
  );
};
export default Projects;
